import { useState } from 'react';
import { toast } from 'react-toastify';

import { routes } from '~/app/routes';
import {
  Checkbox,
  JsonField,
  RadioCardGroup,
  Select,
  TextField,
  Textarea,
  ToggleField,
} from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSection,
  CopyButton,
  FormActions,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { formatLocalTime } from '~/lib/datetime';
import { errorText } from '~/lib/apiError';
import {
  SEND_CHANNELS,
  type SendChannel,
  type SendFormValues,
  initialSendForm,
  PRIORITY_OPTIONS,
  providerKeyOptionLabel,
  sendableKeys,
  sendableTemplates,
  needsTemplateCustomer,
  sendFormSchema,
  templateOptionLabel,
  templateSampleData,
  toSendRequest,
} from '~/lib/notification/sendForm';
import { isDirty } from '~/lib/recordDraft';
import { useSendMessageMutation } from '~/modules/query/notification/messages';
import { useListProviderKeysQuery } from '~/modules/query/notification/providerKeys';
import { useListTemplatesQuery } from '~/modules/query/notification/templates';
import type { SendMessageResponse } from '~/modules/query/notification/types';

const FORM_ID = 'send-test-form';
const CHANNEL_LABELS: Record<SendChannel, string> = { email: 'Email', sms: 'SMS' };

/** `/notifications/send-test`: queue one direct message through the notification service. */
export const SendTestPage = () => {
  const [values, setValues] = useState<SendFormValues>(initialSendForm);
  const [sent, setSent] = useState<SendMessageResponse | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendMessage, { isLoading }] = useSendMessageMutation();
  const keysQuery = useListProviderKeysQuery(undefined, { skip: values.useDefaultProvider });
  const keys = keysQuery.data ?? [];
  const templatesQuery = useListTemplatesQuery(undefined, {
    skip: values.messageSource !== 'template',
  });
  const templates = sendableTemplates(templatesQuery.data ?? []);
  const pickedTemplate = templates.find((template) => template.id === values.templateId);
  const validation = useFormValidation(sendFormSchema(keys, templates), values);
  const guard = useUnsavedGuard(!sent && isDirty(values, initialSendForm));

  const setValue = <K extends keyof SendFormValues>(name: K, value: SendFormValues[K]) =>
    setValues((current) => ({ ...current, [name]: value }));
  const toggleChannel = (channel: SendChannel, on: boolean) =>
    setValue(
      'channels',
      SEND_CHANNELS.filter((c) => (c === channel ? on : values.channels.includes(c))),
    );
  const email = values.channels.includes('email');
  const sms = values.channels.includes('sms');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validation.submit()) return;
    setSendError(null);
    try {
      setSent(await sendMessage(toSendRequest(values, keys, templates)).unwrap());
      toast.success('Test notification queued');
    } catch (err) {
      setSendError(errorText(err, 'The test notification could not be queued. Try again.'));
    }
  };

  return (
    <RecordPageShell
      title="Send test"
      description="Queues one direct message the way a customer's system would send it, so a real recipient receives it."
      breadcrumbs={[
        { label: 'Notifications', href: routes.notificationTemplates },
        { label: 'Send test' },
      ]}
      guard={guard}
      testId="send-test"
    >
      {sent && (
        <Card>
          <CardHeader title="Queued" description="The Worker picks it up from the queue." />
          <CardBody>
            <dl className="grid grid-cols-1 gap-4 text-sm lg:grid-cols-3">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Request ID</dt>
                <dd className="flex items-center gap-2 font-mono text-gray-800 dark:text-white/90">
                  <span data-testid="send-test-request-id">{sent.requestId}</span>
                  <CopyButton value={sent.requestId} label="Copy request ID" />
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Queue item ID</dt>
                <dd className="flex items-center gap-2 font-mono text-gray-800 dark:text-white/90">
                  <span data-testid="send-test-queue-item-id">{sent.queueItemId}</span>
                  <CopyButton value={sent.queueItemId} label="Copy queue item ID" />
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Queued at</dt>
                <dd className="text-gray-800 dark:text-white/90" data-testid="send-test-queued-at">
                  {formatLocalTime(sent.queuedAt)}
                </dd>
              </div>
            </dl>
          </CardBody>
        </Card>
      )}

      <form id={FORM_ID} noValidate onSubmit={submit}>
        <Card>
          <CardBody className="space-y-6">
            {sendError && (
              <Alert variant="error">
                <p className="text-sm text-gray-800 dark:text-white/90">{sendError}</p>
              </Alert>
            )}

            <CardSection title="Channels">
              <div className="flex flex-wrap gap-6">
                {SEND_CHANNELS.map((channel) => (
                  <Checkbox
                    key={channel}
                    id={`send-test-channel-${channel}`}
                    testId={`send-test-channel-${channel}`}
                    text={CHANNEL_LABELS[channel]}
                    checked={values.channels.includes(channel)}
                    onChange={(checked) => toggleChannel(channel, checked)}
                  />
                ))}
              </div>
              {validation.errors.channels && (
                <p className="text-xs text-error-600 dark:text-error-400" role="alert">
                  {validation.errors.channels}
                </p>
              )}
            </CardSection>

            <CardSection
              title="Provider"
              description="Which provider key the Worker sends through. A picked key's customer and sender go on the message, as a customer's own message would carry them."
            >
              <ToggleField
                id="useDefaultProvider"
                testId="send-test-use-default-provider"
                label="Routing"
                text="Use the default provider"
                helperText="No customer or sender on the message, so the default key for each channel sends it."
                checked={values.useDefaultProvider}
                onChange={(on) => setValue('useDefaultProvider', on)}
              />
              {!values.useDefaultProvider && (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {keysQuery.isError && (
                    <Alert
                      variant="error"
                      message={errorText(
                        keysQuery.error,
                        'The provider keys could not be loaded. Try again.',
                      )}
                    />
                  )}
                  {values.channels.map((channel) => {
                    const field = channel === 'email' ? 'emailProviderKeyId' : 'smsProviderKeyId';
                    const options = sendableKeys(keys, channel).map((key) => ({
                      value: key.id,
                      label: providerKeyOptionLabel(key),
                    }));
                    return (
                      <div key={channel} className="space-y-1">
                        <Select
                          id={field}
                          testId={`send-test-${channel}-provider-key`}
                          label={`${CHANNEL_LABELS[channel]} provider key`}
                          required
                          disabled={keysQuery.isLoading}
                          placeholder={
                            options.length
                              ? 'Pick a key'
                              : `No active ${CHANNEL_LABELS[channel]} keys`
                          }
                          options={options}
                          value={values[field]}
                          onChange={(id) => {
                            setValue(field, id);
                            validation.touch(field);
                          }}
                        />
                        {validation.errors[field] && (
                          <p className="text-xs text-error-600 dark:text-error-400" role="alert">
                            {validation.errors[field]}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardSection>

            <CardSection title="Recipient">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {email && (
                  <TextField
                    id="recipientEmail"
                    testId="send-test-recipient-email-input"
                    label="Email address"
                    type="email"
                    required
                    placeholder="sam@example.com"
                    value={values.recipientEmail}
                    onChange={(value) => setValue('recipientEmail', value)}
                    onBlur={() => validation.touch('recipientEmail')}
                    error={validation.errors.recipientEmail}
                  />
                )}
                {sms && (
                  <TextField
                    id="recipientMobile"
                    testId="send-test-recipient-mobile-input"
                    label="Mobile number"
                    required
                    placeholder="+15555550100"
                    value={values.recipientMobile}
                    onChange={(value) => setValue('recipientMobile', value)}
                    onBlur={() => validation.touch('recipientMobile')}
                    error={validation.errors.recipientMobile}
                  />
                )}
              </div>
            </CardSection>

            <CardSection title="Message">
              <RadioCardGroup
                name="messageSource"
                ariaLabel="Message source"
                value={values.messageSource}
                onChange={(source) => setValue('messageSource', source)}
                options={[
                  {
                    value: 'custom',
                    label: 'Custom message',
                    description: 'Type the message for each channel.',
                    testId: 'send-test-source-custom',
                  },
                  {
                    value: 'template',
                    label: 'Template',
                    description: "Send a template's live version, as a customer's system would.",
                    testId: 'send-test-source-template',
                  },
                ]}
              />
              {values.messageSource === 'template' && (
                <div className="space-y-1">
                  {templatesQuery.isError && (
                    <Alert
                      variant="error"
                      message={errorText(
                        templatesQuery.error,
                        'The templates could not be loaded. Try again.',
                      )}
                    />
                  )}
                  <Select
                    id="templateId"
                    testId="send-test-template"
                    label="Template"
                    required
                    disabled={templatesQuery.isLoading}
                    placeholder={templates.length ? 'Pick a template' : 'No live templates'}
                    options={templates.map((template) => ({
                      value: template.id,
                      label: templateOptionLabel(template),
                    }))}
                    value={values.templateId}
                    onChange={(id) => {
                      const template = templates.find((t) => t.id === id);
                      setValues((current) => ({
                        ...current,
                        templateId: id,
                        data:
                          current.data.trim() || !template
                            ? current.data
                            : templateSampleData(template, current.channels),
                      }));
                      validation.touch('templateId');
                    }}
                    helperText="Only live templates are listed. Its subject and from come from the template; blank data is filled from its sample data."
                  />
                  {validation.errors.templateId && (
                    <p className="text-xs text-error-600 dark:text-error-400" role="alert">
                      {validation.errors.templateId}
                    </p>
                  )}
                </div>
              )}
              {values.messageSource === 'template' &&
                needsTemplateCustomer(values, pickedTemplate) && (
                  <div className="space-y-1">
                    <Select
                      id="templateCustomerId"
                      testId="send-test-template-customer"
                      label="Send as customer"
                      required
                      placeholder="Pick a customer"
                      options={(pickedTemplate?.customerIds ?? []).map((id) => ({
                        value: id,
                        label: id,
                      }))}
                      value={values.templateCustomerId}
                      onChange={(id) => {
                        setValue('templateCustomerId', id);
                        validation.touch('templateCustomerId');
                      }}
                      helperText="This template serves several customers. The message carries the one picked, as that customer's system would."
                    />
                    {validation.errors.templateCustomerId && (
                      <p className="text-xs text-error-600 dark:text-error-400" role="alert">
                        {validation.errors.templateCustomerId}
                      </p>
                    )}
                  </div>
                )}
            </CardSection>

            {values.messageSource === 'custom' && (
              <>
                {email && (
                  <CardSection title="Email">
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                      <TextField
                        id="subject"
                        testId="send-test-subject-input"
                        label="Subject"
                        value={values.subject}
                        onChange={(value) => setValue('subject', value)}
                      />
                    </div>
                    <Textarea
                      id="emailMessage"
                      testId="send-test-email-message-input"
                      label="Email message"
                      required
                      rows={6}
                      value={values.emailMessage}
                      onChange={(value) => setValue('emailMessage', value)}
                      onBlur={() => validation.touch('emailMessage')}
                      error={validation.errors.emailMessage}
                    />
                  </CardSection>
                )}

                {sms && (
                  <CardSection title="SMS">
                    <Textarea
                      id="smsMessage"
                      testId="send-test-sms-message-input"
                      label="SMS message"
                      required
                      rows={3}
                      value={values.smsMessage}
                      onChange={(value) => setValue('smsMessage', value)}
                      onBlur={() => validation.touch('smsMessage')}
                      error={validation.errors.smsMessage}
                    />
                  </CardSection>
                )}
              </>
            )}

            <CardSection title="Options">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <Select
                  id="priority"
                  testId="send-test-priority-input"
                  label="Priority"
                  options={PRIORITY_OPTIONS}
                  value={values.priority}
                  onChange={(value) => setValue('priority', value)}
                  helperText="When the queue is busy, ASAP goes out first, then higher priority, then the rest in the order they arrived."
                />
              </div>
              <JsonField
                id="data"
                testId="send-test-data-input"
                label="Data"
                helperText="A JSON object the message can reference, like {{name}}."
                value={values.data}
                onChange={(value) => setValue('data', value)}
                onBlur={() => validation.touch('data')}
                error={validation.errors.data}
              />
            </CardSection>
          </CardBody>
          <CardFooter>
            <FormActions
              form={FORM_ID}
              discardHref={routes.notificationTemplates}
              discardLabel="Cancel"
              saveLabel="Send test"
              busyLabel="Sending…"
              busy={isLoading}
              saveTestId="send-test-submit-button"
            />
          </CardFooter>
        </Card>
      </form>
    </RecordPageShell>
  );
};
